import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { authAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Monitor, Smartphone, Tablet, ShieldCheck, Pencil, Check, Trash2, Eraser } from 'lucide-react';
import { formatDate } from '@/lib/utils';

const CONTROLLABLE_ROLES = [
    { value: 'operator', label: 'Opérateurs' },
    { value: 'production', label: 'Production' },
    { value: 'manager', label: 'Managers' },
    { value: 'supervisor', label: 'Superviseurs' },
    { value: 'chef_cuisine', label: 'Chefs de Cuisine' },
    { value: 'livreur', label: 'Livreurs' },
    { value: 'facturation', label: 'Facturation' },
    { value: 'comptabilite', label: 'Comptabilité' },
    { value: 'readonly', label: 'Lecture seule' },
];

const DeviceIcon = ({ type }) => {
    if (type === 'mobile') return <Smartphone className="w-4 h-4 text-slate-400" />;
    if (type === 'tablette') return <Tablet className="w-4 h-4 text-slate-400" />;
    return <Monitor className="w-4 h-4 text-slate-400" />;
};

export default function DevicesTab() {
    const [devices, setDevices] = useState([]);
    const [control, setControl] = useState({ enabled: true, roles: ['operator'] });
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [editing, setEditing] = useState(null); // {device_id, name}

    const load = useCallback(async () => {
        try {
            const [d, c] = await Promise.all([authAPI.getDevices(), authAPI.getDeviceControl()]);
            setDevices(d.data);
            setControl(c.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur de chargement');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const saveControl = async (next) => {
        setControl(next);
        try {
            await authAPI.setDeviceControl(next);
            toast.success('Réglages enregistrés');
        } catch { toast.error('Erreur d\'enregistrement'); }
    };

    const toggleApproval = async (dev, approved) => {
        setDevices(prev => prev.map(d => d.device_id === dev.device_id ? { ...d, approved } : d));
        try {
            await authAPI.setDeviceApproval(dev.device_id, approved);
            toast.success(approved ? 'Appareil autorisé' : 'Appareil bloqué');
        } catch {
            toast.error('Erreur');
            setDevices(prev => prev.map(d => d.device_id === dev.device_id ? { ...d, approved: !approved } : d));
        }
    };

    const deleteDevice = async (dev) => {
        try {
            await authAPI.deleteDevice(dev.device_id);
            setDevices(prev => prev.filter(d => d.device_id !== dev.device_id));
            toast.success('Appareil supprimé');
        } catch { toast.error('Erreur'); }
    };

    const dedupe = async () => {
        try {
            const res = await authAPI.dedupeDevices();
            toast.success(`${res.data.deleted} doublon(s) supprimé(s)`);
            load();
        } catch { toast.error('Erreur'); }
    };

    const saveName = async () => {
        try {
            await authAPI.renameDevice(editing.device_id, editing.name);
            setDevices(prev => prev.map(d => d.device_id === editing.device_id ? { ...d, name: editing.name } : d));
            setEditing(null);
        } catch { toast.error('Erreur'); }
    };

    const filtered = devices.filter(d => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return (d.name || '').toLowerCase().includes(q)
            || (d.last_username || '').toLowerCase().includes(q)
            || (d.os || '').toLowerCase().includes(q)
            || (d.browser || '').toLowerCase().includes(q);
    });

    const approvedCount = devices.filter(d => d.approved).length;

    if (loading) return <div className="py-12 text-center text-slate-400">Chargement…</div>;

    return (
        <div className="space-y-4 mt-4" data-testid="devices-tab">
            <Card className="border-amber-200 bg-amber-50/40">
                <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                        <ShieldCheck className="w-5 h-5 text-amber-600" />
                        Contrôle des appareils
                    </CardTitle>
                    <CardDescription>
                        Les utilisateurs des rôles cochés ne peuvent se connecter que depuis un appareil autorisé ci-dessous.
                        Les administrateurs ne sont jamais bloqués.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                        <div className="flex items-center gap-2">
                            <Switch
                                checked={!!control.enabled}
                                onCheckedChange={(v) => saveControl({ ...control, enabled: v })}
                                data-testid="device-control-enabled-switch"
                            />
                            <span className="text-sm font-medium">{control.enabled ? 'Contrôle activé' : 'Contrôle désactivé'}</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-4">
                            {CONTROLLABLE_ROLES.map(r => (
                                <label key={r.value} className="flex items-center gap-1.5 text-sm cursor-pointer">
                                    <Checkbox
                                        checked={(control.roles || []).includes(r.value)}
                                        onCheckedChange={(v) => saveControl({
                                            ...control,
                                            roles: v ? [...(control.roles || []), r.value] : (control.roles || []).filter(x => x !== r.value),
                                        })}
                                        data-testid={`device-control-role-${r.value}`}
                                    />
                                    {r.label}
                                </label>
                            ))}
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <span className="text-sm text-slate-600 whitespace-nowrap">Email d'alerte nouvel appareil :</span>
                        <Input
                            type="email"
                            placeholder="votre@email.com (vide = pas d'alerte)"
                            value={control.notify_email || ''}
                            onChange={(e) => setControl({ ...control, notify_email: e.target.value })}
                            onBlur={() => saveControl(control)}
                            className="max-w-xs h-8"
                            data-testid="device-control-notify-email-input"
                        />
                        <span className="text-xs text-slate-400">Un email par nouvel appareil bloqué (pas de doublon)</span>
                    </div>
                </CardContent>
            </Card>

            <div className="flex items-center justify-between gap-3">
                <Input
                    placeholder="Rechercher un appareil, un utilisateur…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="max-w-xs"
                    data-testid="devices-search-input"
                />
                <span className="flex items-center gap-3">
                    <Button variant="outline" size="sm" onClick={dedupe} data-testid="devices-dedupe-btn">
                        <Eraser className="w-4 h-4 mr-1.5" />
                        Nettoyer les doublons
                    </Button>
                    <span className="text-sm text-slate-500">{approvedCount} autorisé(s) / {devices.length} appareils</span>
                </span>
            </div>

            <Card>
                <CardContent className="p-0 overflow-x-auto">
                    <table className="w-full data-table">
                        <thead>
                            <tr className="table-header">
                                <th className="text-left px-3 py-2">Appareil</th>
                                <th className="text-left px-3 py-2">Système</th>
                                <th className="text-left px-3 py-2">Dernier utilisateur</th>
                                <th className="text-left px-3 py-2">Dernière connexion</th>
                                <th className="text-center px-3 py-2">Accès</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(d => (
                                <tr key={d.device_id} className={`table-row ${d.approved ? '' : 'bg-red-50/40'}`} data-testid={`device-row-${d.device_id}`}>
                                    <td className="px-3 py-1.5">
                                        <div className="flex items-center gap-2">
                                            <DeviceIcon type={d.device_type} />
                                            {editing?.device_id === d.device_id ? (
                                                <span className="flex items-center gap-1">
                                                    <Input
                                                        value={editing.name}
                                                        onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                                                        className="h-7 w-40 text-sm"
                                                        onKeyDown={(e) => e.key === 'Enter' && saveName()}
                                                        autoFocus
                                                    />
                                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={saveName}><Check className="w-4 h-4" /></Button>
                                                </span>
                                            ) : (
                                                <span className="flex items-center gap-1.5">
                                                    <span className="font-medium">{d.name || <span className="text-slate-400 italic">Sans nom</span>}</span>
                                                    <button className="text-slate-300 hover:text-slate-600" onClick={() => setEditing({ device_id: d.device_id, name: d.name || '' })} title="Renommer">
                                                        <Pencil className="w-3 h-3" />
                                                    </button>
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-3 py-1.5 text-slate-600 text-xs">{d.os} · {d.browser} · {d.screen}</td>
                                    <td className="px-3 py-1.5">{d.last_username || '—'}</td>
                                    <td className="px-3 py-1.5 text-slate-500 text-xs">{d.last_seen ? formatDate(d.last_seen) : '—'}</td>
                                    <td className="px-3 py-1.5 text-center">
                                        <div className="inline-flex items-center gap-2">
                                            <Switch
                                                checked={!!d.approved}
                                                onCheckedChange={(v) => toggleApproval(d, v)}
                                                data-testid={`device-approval-switch-${d.device_id}`}
                                            />
                                            {d.approved
                                                ? <Badge className="bg-emerald-100 text-emerald-700">Autorisé</Badge>
                                                : <Badge className="bg-red-100 text-red-700">Bloqué</Badge>}
                                            <button
                                                className="text-slate-300 hover:text-red-500 ml-1"
                                                onClick={() => deleteDevice(d)}
                                                title="Supprimer cet appareil"
                                                data-testid={`device-delete-btn-${d.device_id}`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {filtered.length === 0 && (
                        <div className="py-8 text-center text-slate-400 text-sm">Aucun appareil</div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
